import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { ArcPlcIcon } from "components/Icons/Icons";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import dayjs from "dayjs";

import { getFarmPaymentCategoryLabel } from "./FarmPaymentConstants";
import useFarmPayments from "./useFarmPayments";

export default function FarmPaymentListWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const navigate = useNavigate();
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);

  const { farmPayments, isLoading, error, remove } = useFarmPayments({ year: currentYear });

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: { key: "crop_year", value: currentYear },
    });
  }, [currentYear, globalDispatch]);

  const handleAdd = () => {
    navigate("/arcplc/payments/0");
  };

  const handleEdit = (payment) => {
    navigate(`/arcplc/payments/${payment.id}`);
  };

  const handleDelete = async (payment) => {
    await remove(payment);
  };

  return (
    <>
      <WorkspaceHeader
        title="Payments"
        category={{
          title: "ARC/PLC",
          icon: <ArcPlcIcon />,
        }}
        breadCrumbs={[
          {
            title: "ARC/PLC",
            path: "/arcplc",
          },
        ]}
      />

      <WorkspacePageHeader currentYear={currentYear} onYearChange={setCurrentYear} isLoading={isLoading} />

      {error && <Alert severity="error">{error.message || "Error loading payments"}</Alert>}

      {isLoading || !farmPayments?.length ? (
        <Box className="cardContainer">
          <EmptyCard
            text={isLoading ? "Loading Payments..." : "No Payments Found"}
            secondaryText={isLoading ? `Loading data for ${currentYear}` : `Add a payment for ${currentYear}`}
            isLoading={isLoading}
          />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Commodity</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {farmPayments.map((payment) => (
                <TableRow
                  key={payment.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleEdit(payment)}
                >
                  <TableCell>{getFarmPaymentCategoryLabel(payment.payment_category)}</TableCell>
                  <TableCell>{payment.commodity_name || "—"}</TableCell>
                  <TableCell align="right">
                    {Number(payment.payment_amount ?? 0).toLocaleString(undefined, {
                      style: "currency",
                      currency: "USD",
                    })}
                  </TableCell>
                  <TableCell>
                    {payment.payment_date ? dayjs(payment.payment_date).format("MM/DD/YYYY") : "—"}
                  </TableCell>
                  <TableCell>{payment.note || "—"}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Delete payment"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDelete(payment);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && farmPayments?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">{farmPayments.length} payment(s)</Typography>
        </Box>
      )}

      <WorkspaceActionBar
        content={
          <Button variant="contained" onClick={handleAdd}>
            <AddCircleOutline sx={{ mr: 1 }} />
            Add Payment
          </Button>
        }
      />
    </>
  );
}
